import ScrollReveal from '@/components/base/ScrollReveal';
import SectionHeading from '@/components/base/SectionHeading';
import CostCalculator from '@/pages/forex-trading/components/CostCalculator';
import { costImpactScenarios, costTable, executionCosts } from '@/mocks/forexTrading';

export default function ExecutionCostGuide() {
  return (
    <section className="border-y border-background-200 bg-background-100/60">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20 lg:px-10">
        <SectionHeading
          eyebrow="ต้นทุนการเทรด"
          title="เงินที่หายไปก่อนราคาจะเดิน"
          subtitle="ทุกออเดอร์มีต้นทุนที่ถูกหักทันทีและระหว่างทาง รู้ว่ามันมาจากไหน จะได้ไม่ตกใจตอนเห็นตัวเลขติดลบทันทีหลังกด"
        />

        <div className="mt-9 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {executionCosts.map((cost, index) => (
            <ScrollReveal key={cost.label} delay={index * 70} className="h-full">
              <article className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                <span
                  className={`flex h-9 w-9 items-center justify-center rounded-md ${
                    cost.accent ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-800'
                  }`}
                >
                  <i className={`${cost.icon} text-base`} />
                </span>
                <h3 className="mt-3.5 font-heading text-base font-semibold text-foreground-950">{cost.label}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{cost.desc}</p>
                {cost.extra ? (
                  <p className="mt-2.5 rounded-md bg-background-100/80 px-3 py-2 text-xs leading-relaxed text-foreground-600">
                    {cost.extra}
                  </p>
                ) : null}
                <p className="mt-auto border-t border-background-200/70 pt-3.5 font-num text-xs leading-relaxed text-foreground-800">
                  {cost.example}
                </p>
              </article>
            </ScrollReveal>
          ))}
        </div>

        <ScrollReveal className="mt-8">
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="flex flex-col gap-1 border-b border-background-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
                ต้นทุนรวมต่อ 1 ล็อต (เปิด-ปิดหนึ่งรอบ)
              </h3>
              <p className="text-xs text-foreground-500">คู่เงินเดียวกัน เทียบบัญชี Standard กับ ECN · USD/JPY อ้างอิง ≈ 150</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px] border-collapse text-left">
                <thead>
                  <tr className="bg-background-100/70 text-xs text-foreground-500">
                    <th rowSpan={2} className="px-5 py-3 align-bottom font-medium">
                      คู่เงิน
                    </th>
                    <th
                      colSpan={2}
                      className="border-l border-background-200 px-5 pb-1.5 pt-3 text-center text-[11px] font-medium uppercase tracking-wide"
                    >
                      บัญชี Standard
                    </th>
                    <th
                      colSpan={3}
                      className="border-l border-background-200 px-5 pb-1.5 pt-3 text-center text-[11px] font-medium uppercase tracking-wide text-primary-700"
                    >
                      บัญชี ECN
                    </th>
                  </tr>
                  <tr className="bg-background-100/70 text-[11px] uppercase tracking-wide text-foreground-500">
                    <th className="border-l border-background-200 px-5 pb-3 pt-1.5 font-medium">Spread</th>
                    <th className="px-5 pb-3 pt-1.5 font-medium">ต้นทุนรวม</th>
                    <th className="border-l border-background-200 px-5 pb-3 pt-1.5 font-medium">Spread</th>
                    <th className="px-5 pb-3 pt-1.5 font-medium">คอมมิชชัน</th>
                    <th className="px-5 pb-3 pt-1.5 font-medium">ต้นทุนรวม</th>
                  </tr>
                </thead>
                <tbody>
                  {costTable.map((row) => (
                    <tr key={row.pair} className="border-t border-background-200/80">
                      <td className="px-5 py-3.5 font-num text-sm font-semibold text-foreground-950">{row.pair}</td>
                      <td className="border-l border-background-200/70 px-5 py-3.5 font-num text-sm text-foreground-700">
                        {row.standard.spread}
                      </td>
                      <td className="px-5 py-3.5 font-num text-sm text-foreground-700">{row.standard.total}</td>
                      <td className="border-l border-background-200/70 px-5 py-3.5 font-num text-sm text-foreground-700">
                        {row.ecn.spread}
                      </td>
                      <td className="px-5 py-3.5 font-num text-sm text-foreground-700">{row.ecn.commission}</td>
                      <td className="px-5 py-3.5 font-num text-sm font-semibold text-accent-700">{row.ecn.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <CostCalculator />
            <p className="border-t border-background-200 bg-background-100/40 px-5 py-3 text-xs leading-relaxed text-foreground-500">
              สองบัญชีมีต้นทุนรวมต่อล็อตต่างกันไม่มาก ส่วนต่างจริงอยู่ที่คุณภาพการจับคู่และความเร็ว ถ้าถือออเดอร์สั้น ๆ ค่าคอมมิชชันแบบ ECN จะกินกำไรหนักกว่าที่คิด
              ตัวเลขทั้งหมดเป็นเพียงตัวอย่าง ต้นทุนจริงขึ้นอยู่กับโบรกเกอร์ ประเภทบัญชี และสภาพคล่องในแต่ละช่วงเวลา
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal className="mt-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
                  ต้นทุนเดิม แต่กินกำไรต่างกัน
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-foreground-500">
                  สมมติ Spread 0.8 pip คงที่ ยิ่งตั้งเป้าไกล ต้นทุนยิ่งกินสัดส่วนน้อยลง
                </p>
              </div>
              <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1.5 font-num text-[11px] font-medium text-secondary-900">
                <i className="ri-line-chart-line text-xs" />
                EUR/USD · 1 ล็อต
              </span>
            </div>

            <ul className="mt-6 space-y-5">
              {costImpactScenarios.map((scenario) => (
                <li key={scenario.label} className="grid gap-2 sm:grid-cols-[9.5rem_1fr] sm:items-center sm:gap-5">
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{scenario.label}</p>
                    <p className="mt-0.5 font-num text-xs text-foreground-500">
                      เป้า {scenario.target} · ต้นทุน {scenario.cost}
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-background-200/80">
                        <div
                          className="h-full rounded-full bg-accent-500"
                          style={{ width: scenario.barWidth }}
                        />
                      </div>
                      <span className="w-14 shrink-0 text-right font-num text-sm font-semibold text-accent-700">
                        {scenario.percent}
                      </span>
                    </div>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{scenario.note}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </ScrollReveal>

        <ScrollReveal className="mt-4">
          <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-secondary-100/60 px-5 py-4 sm:flex-row sm:items-center">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-50 text-secondary-800">
              <i className="ri-eye-line text-base" />
            </span>
            <p className="text-xs leading-relaxed text-foreground-700 sm:text-sm">
              <strong className="font-semibold text-foreground-950">เช็ก Spread จริงก่อนกด:</strong> เปิดหน้าต่าง
              Market Watch แล้วดูผลต่างระหว่าง Bid กับ Ask หรือเปิดหน้าต่าง New Order ที่จะขึ้นต้นทุนจริงก่อนยืนยัน
              ถ้าตัวเลขกว้างกว่าปกติหลายเท่า ให้ข้ามไม้นั้นไปก่อน
            </p>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}